import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { fichaIncludes, gallery, primaryCta } from '../data/content';
const ease = [0.23, 1, 0.32, 1] as const;
export function Gallery() {
  const [index, setIndex] = useState(0);
  const total = gallery.length;
  const goTo = (next: number) => {
    setIndex((next + total) % total);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(index - 1);
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(index + 1);
    }
  };
  const current = gallery[index];
  return <section id="muestras" className="w-full scroll-mt-4 bg-gray-900 py-16 text-white md:py-24" data-id="element-103" data-mp="26x2nju165878">
      <div className="mx-auto w-full max-w-5xl px-5 md:px-8" data-id="element-104" data-mp="21xzhc69gcvxo">
        <header className="mx-auto max-w-4xl text-center" data-id="element-105" data-mp="21xg95h9ltlwu">
          <p className="text-xs font-bold uppercase tracking-widest text-blue-300" data-id="element-106" data-mp="rd2xd2wa1wz1">
            Mira el Atlas por dentro
          </p>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight md:text-5xl" data-id="element-107" data-mp="wa31h89kgpni">
            Aprende a reconocer, comparar y diferenciar
          </h2>
          <p className="mt-6 text-lg text-gray-300" data-id="element-108" data-mp="1547n9d9ufaw6">
            Cada ficha está diseñada para mostrarte rápidamente:
          </p>
        </header>

        <ul className="mx-auto mt-6 grid max-w-4xl gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-109" data-mp="8frwxeywsxi0">
          {fichaIncludes.map(item => <li key={item} className="flex items-start gap-2.5 text-gray-100" data-id="element-110" data-mp="hdvha7y5ro88">
              <CheckIcon className="mt-1 h-4 w-4 shrink-0 text-emerald-400" aria-hidden="true" data-id="element-111" data-mp="mdgjcr8mkcp6" />
              <span data-id="element-112" data-mp="1stk7d86pl3d9">{item}</span>
            </li>)}
        </ul>

        <div role="group" aria-roledescription="carrusel" aria-label="Galería de fichas visuales" tabIndex={0} onKeyDown={handleKeyDown} className="relative mt-12 rounded-xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400" data-id="element-113" data-mp="1stabj3yzg1zn">
          <div className="overflow-hidden rounded-xl" data-id="element-114" data-mp="7ojpwhktcanm">
            <motion.div className="flex cursor-grab active:cursor-grabbing" animate={{
            x: `-${index * 100}%`
          }} transition={{
            duration: 0.3,
            ease
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.12} onDragEnd={(_, info) => {
            if (info.offset.x < -60) goTo(index + 1);
            if (info.offset.x > 60) goTo(index - 1);
          }} data-id="element-115" data-mp="7n8jsb3waqtc">
              {gallery.map((item, itemIndex) => <figure key={item.title} className="w-full shrink-0" aria-hidden={itemIndex !== index} data-id="element-116" data-mp="3g2jxfah26pti" data-mpi={itemIndex}>
                  <img src={item.image} alt={`Ficha visual del Atlas: ${item.title}`} className="pointer-events-none w-full rounded-xl bg-white" draggable={false} loading={itemIndex === 0 ? 'eager' : 'lazy'} data-id="element-117" data-mp="2nffqgzyeo1f3" />
                </figure>)}
            </motion.div>
          </div>

          <button type="button" onClick={() => goTo(index - 1)} aria-label="Ficha anterior" className="absolute left-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-800 shadow-lg transition-colors duration-150 ease-out hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 md:-left-5 md:h-12 md:w-12" data-id="element-118" data-mp="xwhhuq4by2la">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-119" data-mp="179udck5hjeq" />
          </button>
          <button type="button" onClick={() => goTo(index + 1)} aria-label="Ficha siguiente" className="absolute right-2 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-800 shadow-lg transition-colors duration-150 ease-out hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-300 md:-right-5 md:h-12 md:w-12" data-id="element-120" data-mp="67xm60g7e4a0">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-121" data-mp="7njt1k166c8b" />
          </button>
        </div>

        <div className="mx-auto mt-7 max-w-4xl text-center" aria-live="polite" data-id="element-122" data-mp="14djak3zasl31">
          <p className="text-xl font-extrabold tracking-tight md:text-2xl" data-id="element-123" data-mp="zhlqpnotswli">{current.title}</p>
          <p className="mt-2 text-gray-300" data-id="element-124" data-mp="vde17oajncm8">{current.caption}</p>
        </div>

        <div className="mt-7 flex items-center justify-center gap-3" data-id="element-125" data-mp="qfcix5kxzhbt">
          {gallery.map((item, itemIndex) => <button key={item.title} type="button" onClick={() => goTo(itemIndex)} aria-label={`Ver ${item.title}`} aria-current={itemIndex === index} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ease-out focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-400 ${itemIndex === index ? 'w-8 bg-white' : 'w-2.5 bg-white/35 hover:bg-white/60'}`} data-id="element-126" data-mp="3g6r9z1kltmu6" data-mpi={itemIndex} />)}
        </div>

        <p className="mt-6 text-center text-sm text-gray-400" data-id="element-127" data-mp="29fbtu4mxfjo5">
          Desliza para ver el material por dentro — {index + 1} de {total}
        </p>

        <div className="mx-auto mt-10 max-w-2xl text-center" data-id="element-128" data-mp="2ec29iqutkd91">
          <CtaButton size="lg" className="w-full" data-id="element-129" data-mp="2je1c3yiqj64x">
            {primaryCta}
          </CtaButton>
          <p className="mt-3 text-sm text-gray-400" data-id="element-130" data-mp="2ndjw8s2qfd6p">
            Acceso inmediato · PDF imprimible · 6 Tomos + 5 Bonos
          </p>
        </div>

        <p className="mx-auto mt-14 max-w-3xl text-center text-2xl font-extrabold italic leading-snug tracking-tight text-white md:text-3xl" data-id="element-131" data-mp="p11mrdojyxkg">
          No solo ves el instrumento: aprendes qué detalle observar para diferenciarlo de otros
          similares.
        </p>
      </div>
    </section>;
}